import React, { FC, useState } from 'react';
import { BlueButton, Box } from '~Styles';
import { ErrorBox, FormatInput, GoBackButton } from '../../styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useDeviceHandler } from '~Components/DeviceProvider';
import {
  ONBOARDING_EVENT_STEP,
  useButton,
  useDispatch,
  useI18n,
  useLogger,
  useMetadata,
  useOnboardingTracker,
  useSelector,
} from '~Hooks';
import Loader from '~ComponentsImproved/Loader';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client';
import { ACTIVE_SKV_DEVICE, CREATE_POS_DEVICE } from '../../api/mutations';
import {
  ActivateSkvDevice,
  ActivateSkvDeviceVariables,
  CreatePOSDevice,
  CreatePOSDeviceVariables,
} from '~Components/ClaimDevice/api/__queries__';
import { useStorage } from '~Components/Storage';

const Wrapper = styled(Box)`
  flex: 1;
  padding-top: 4px;
  overflow: hidden;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

interface Props {
  onComplete: () => void;
  goBack: () => void;
}

/** Creates a new device & claims it */
const Create: FC<Props> = ({ onComplete, goBack }) => {
  const { i18n } = useI18n();

  const logger = useLogger('claim-device');
  const deviceHandler = useDeviceHandler();
  const onbTracker = useOnboardingTracker();
  const metadata = useMetadata();
  const [, updateStorage] = useStorage();

  const venueId = useSelector((state) => state.venueId) as string;
  const companyId = useSelector((state) => state.company);
  const country = useSelector((state) => state.country);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);

  /* company.country is undefined for most companie and in those cases we should assume its sweden. */
  const isSwedishCompany = country === 'SE' || (!country && defaultLanguage === 'se');

  const [activateSkvDevice] = useMutation<ActivateSkvDevice, ActivateSkvDeviceVariables>(
    ACTIVE_SKV_DEVICE,
  );
  const [createPOSDevice] = useMutation<CreatePOSDevice, CreatePOSDeviceVariables>(
    CREATE_POS_DEVICE,
  );

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [nameInput, setNameInput] = useState<string>('');

  const handleCreateDevice = async () => {
    setLoading(true);

    const isFirstDevice = deviceHandler.allDevices.length === 0;

    logger.info('Creating device with name: ' + nameInput);

    if (!metadata.data.zip) {
      logger.error('No zip found');
      return;
    }

    try {
      /** Finally create the device */
      const newlyCreatedDeviceId = await deviceHandler.createDevice(nameInput);

      if (!newlyCreatedDeviceId) {
        throw new Error('Failed to create device');
      }

      let newlyCreatedComplianceId: string | undefined;
      let newlyCreatedProductionNumber: string | undefined;

      /* This is needed for Skatteverket compliance - we need complianceId */
      if (isSwedishCompany) {
        const activateSkvDeviceRes = await activateSkvDevice({
          variables: {
            corporateId: metadata.data.organizationId,
            deviceId: newlyCreatedDeviceId,
            deviceName: nameInput,
            street: metadata.data.street,
            city: metadata.data.city,
            zip: metadata.data.zip,
            legalName: metadata.data.legalName,
          },
        });

        newlyCreatedComplianceId = activateSkvDeviceRes.data?.activateSkvDevice?.controlUnitSerial;
        newlyCreatedProductionNumber =
          activateSkvDeviceRes.data?.activateSkvDevice?.productionNumber;

        if (!newlyCreatedComplianceId) {
          /** Can be due to incorrectly formatted venue form (this should have been validated in backoffice) */
          throw new Error(i18n('ClaimDevice.Create.PotentialVenueError'));
        }
      }

      /** Create a POS device to get its ID */
      const posDeviceRes = await createPOSDevice({
        variables: {
          name: nameInput,
          venueId,
          companyId,
          lastSeen: new Date().toISOString(),
          isForDeviceSettings: true,
        },
      });

      const newlyCreatedPosDeviceId = posDeviceRes.data?.createPOSDevice?.id;

      if (!newlyCreatedPosDeviceId) {
        throw new Error(i18n('ClaimDevice.Create.FailedToCreatePOSDeviceError'));
      }

      const updateDeviceRes = await deviceHandler.updateDevice(
        newlyCreatedDeviceId,
        newlyCreatedPosDeviceId,
        newlyCreatedComplianceId,
        newlyCreatedProductionNumber,
      );

      if (!updateDeviceRes.success) {
        throw new Error(i18n('ClaimDevice.Create.FailedToUpdateDevice'));
      }

      /** Finally claim the newly created device by passing in the id */
      const claimDeviceRes = await deviceHandler.claim(newlyCreatedDeviceId);

      if (!claimDeviceRes.success) {
        throw new Error('Failed to claim device');
      }

      if (isFirstDevice) {
        onbTracker.event(ONBOARDING_EVENT_STEP.CLAIMED_DEVICE);
      }

      updateStorage({
        taxComplianceDeviceId: newlyCreatedPosDeviceId,
        taxComplianceDeviceName: nameInput,
        taxComplianceCorporateId: metadata.data.organizationId,
      });

      onComplete();
    } catch (error) {
      logger.error(
        '(Failed to Create Device) - ' +
          (error
            ? typeof error === 'object' && 'message' in error && typeof error.message === 'string'
              ? error.message
              : JSON.stringify(error)
            : i18n('General.Error')),
      );
      setError(
        error
          ? typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? error.message
            : i18n('General.Error')
          : i18n('General.Error'),
      );
    }

    setLoading(false);
  };

  const missingValuesMap = {
    street: !metadata.data.street ? 'street' : '',
    city: !metadata.data.city ? 'city' : '',
    zip: !metadata.data.zip ? 'zip' : '',
    legalName: !metadata.data.legalName ? 'legalName' : '',
    organizationId: !metadata.data.organizationId ? 'organizationId' : '',
  };

  // Create an array of non-empty missing values
  const missingValues = Object.values(missingValuesMap).filter((value) => value !== '');

  const isValid = {
    length: nameInput.length >= 3 && nameInput.length <= 20,
    name: /^[a-zA-Z0-9]+$/.test(nameInput),
    unique: !deviceHandler.allDevices.find((d) => d.name === nameInput),
    allVenueValuesExist: missingValues.length === 0,
  };

  const goBackButton = useButton({
    onClick: () => goBack(),
    disabled: loading,
  });

  const submitButton = useButton({
    onClick: handleCreateDevice,
    disabled:
      !isValid.length ||
      !isValid.name ||
      !isValid.unique ||
      !isValid.allVenueValuesExist ||
      loading,
  });

  return (
    <Wrapper display="flex" flexDirection="column" alignItems="center" fluid>
      <FormatInput
        value={nameInput}
        onChange={(e) => {
          if (error) {
            setError(null);
          }
          setNameInput(e.target.value.toLowerCase());
        }}
        type="text"
        placeholder={i18n('ClaimDevice.Create.Placeholder')}
      />

      <Box
        display="flex"
        flexDirection="row"
        gap="xs"
        marginTop="medium"
        alignItems="center"
        fluid>
        <GoBackButton fluid {...goBackButton}>
          {i18n('General.GoBack')}
        </GoBackButton>

        <BlueButton fluid {...submitButton}>
          {loading ? (
            <Loader size="sm" color="var(--colorWhite)" />
          ) : (
            i18n('ClaimDevice.CreateAndUse')
          )}
        </BlueButton>
      </Box>

      {!loading && nameInput.length > 0 && (
        <ErrorBox padding="small" display="flex" flexDirection="column" gap="xs">
          {!isValid.length && !error && (
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {i18n('ClaimDevice.Create.NameNotValid')}
            </Paragraph>
          )}
          {!isValid.name && !error && (
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {i18n('ClaimDevice.Create.NameOnlyLettersAndNumber')}
            </Paragraph>
          )}
          {!isValid.unique && !error && (
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {i18n('ClaimDevice.Create.NameAlreadyExists')}
            </Paragraph>
          )}
          {!isValid.allVenueValuesExist && !error && (
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {`${i18n('ClaimDevice.Create.MissingVenueInfo')}: ${missingValues.join(', ')}`}
            </Paragraph>
          )}
          {error && (
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {error}
            </Paragraph>
          )}
        </ErrorBox>
      )}
    </Wrapper>
  );
};

export default Create;
